import { useEffect, useState } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet.vectorgrid";
import { ALL_STATE, COLOR, COMBINED_LEVELS_FILTER, SCREEN_TYPE } from "../../utils/Constants.tsx";
import { getColorCode, getThresholds, sumAll } from "../../utils/Util.tsx";
import { LegendStateValuesType } from "../../utils/Types.tsx";
import { paths } from "../../services/ServiceConstants.tsx";

const getStateAndDistrictGridColor = (
  properties: any,
  stateId: string,
  filterName: any,
  value: any,
  districtId?: string,
) => {
  if((filterName === COMBINED_LEVELS_FILTER.Claims_Rejected || COMBINED_LEVELS_FILTER.Claims_Repudiated ||COMBINED_LEVELS_FILTER.Claims_Cwp ) && value === 0) {
    return COLOR.state.max;
  } 
  if (districtId && String(districtId) === String(properties.did)) { 
    return COLOR.white;
  } else if (!districtId && stateId === ALL_STATE) {
    return COLOR.white;
  } else if (!districtId && String(stateId) === String(properties.sid)) {
    return COLOR.white;
  } else {
    return COLOR.transparent;
  }
};

/**
 * This method returns color code to fill the color on Map.
 * thresholds values are used to find the color code.
 * @param gridId it is state id
 * @param idWiseData this is key value pair. It store state id and respective state information
 * @legendValues
 * @returns it return color code
 */
export const getColorByGridId = (
  properties: any,
  idWiseData: any,
  legendValues: any,
  stateId: string,
  screenType: string,
  districtId?: string,
  filterName?: any
) => {
  if (!idWiseData) {
    return COLOR.white;
  }
  const gridid = properties?.gridid;
  const data = idWiseData[gridid];
  let thresholds = getThresholds(data?.levelOne, data?.levelTwo, legendValues);
  let dataKey = thresholds.key;
  let value: any;
  if (dataKey === "payout_amounts") {
    value = sumAll(data[dataKey]);
  } else if (dataKey) {
    value = data[dataKey];
  }

  if (!value || !thresholds?.minMax) {
    return getStateAndDistrictGridColor(properties, stateId, filterName, value, districtId,);
  }
  const code = getColorCode(
    value,
    thresholds?.minMax.min,
    thresholds?.minMax.max,
    screenType === SCREEN_TYPE.NOC ? COLOR.country.min : COLOR.state.min,
    screenType === SCREEN_TYPE.NOC ? COLOR.country.max : COLOR.state.max
  );
  return code;
};

interface StateVectorLayerProps {
  stateId: string;
  districtId?: string;
  idWiseData: any;
  legendValues: LegendStateValuesType;
  selectedGeometry: any;
  mapCenter: any;
  gridCenter: any;
  screenType: string;
  setVectorTileLayer: (value: any) =>void;
  setMapClickedInfo: (value: any) => void;
  onSelectorAdded: (value: boolean) => void;
  clearLayers: (map : any)=> void;
  filterName : any
}

const StateVectorLayer = ({
  stateId,
  districtId,
  idWiseData,
  legendValues,
  selectedGeometry,
  mapCenter,
  gridCenter,
  screenType,
  setVectorTileLayer,
  setMapClickedInfo,
  onSelectorAdded,
  clearLayers,
  filterName
}: StateVectorLayerProps) => {
  const map = useMap();
  const [selectorPolygon, setSelectorPolygon] = useState(null);
  useEffect(() => {
    if (map && idWiseData) {
      clearLayers(map);
      addVectorLayer(false);
      if (screenType === SCREEN_TYPE.NOC) {
        addVectorLayer(true);
        map.setZoom(5);
      } else if (districtId) {
        map.setZoom(8);
      } else {
        map.setZoom(6);
      }
    }
  }, [idWiseData]);

  useEffect(() => {
    if (map && gridCenter) {
      map.setView(gridCenter);
      const timer = setTimeout(() => {
        map.setZoom(10);
      }, 400);
      return () => clearTimeout(timer);
    } else if (map && !gridCenter && mapCenter) {
      map.setView(mapCenter);
      const timer = setTimeout(() => {
        map.setZoom(6);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [gridCenter]);

  useEffect(() => {
    if (selectorPolygon) {
      onSelectorAdded(true);
    }
  }, [selectorPolygon]);

  const addSelector = () => {
    if (selectorPolygon) selectorPolygon.remove();
    setSelectorPolygon(null);
    if (selectedGeometry) {
      onSelectorAdded(false);
      const polygon = L.polygon(selectedGeometry);
      polygon.addTo(map);
      setSelectorPolygon(polygon);
    }
  };
  /**
   * Add vector tile layers
   */
  const addVectorLayer = (isScreenTypeNoc) => {
    if (mapCenter && !gridCenter) {
      map.setView(mapCenter);
    }
    L.DomEvent.fakeStop = function () {
      return true;
    };
    const fetchOptions = {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("AUTH_TOKEN"),
      },
    };
    const vectorTileLayer = L.vectorGrid.protobuf(
      isScreenTypeNoc
        ? paths.COUNTRY_STATE_MBTILE_URL
        : paths.STATE_VIEW_MBTILE_URL,
      {
        fetchOptions,
        rendererFactory: L.canvas.tile,
        interactive: true,
        getFeatureId: function (feature) {
          return feature.properties.id;
        },
        vectorTileLayerStyles: {
          grid_april14: function (properties, zoom) {
            const colorCode = getColorByGridId(
              properties,
              idWiseData,
              legendValues,
              stateId,
              screenType,
              districtId,
              filterName
            );
            return {
              fill: true,
              fillColor: colorCode,
              fillOpacity: 1,
              stroke: true,
              color:
                screenType === SCREEN_TYPE.NOC &&
                colorCode !== COLOR.white &&
                colorCode !== COLOR.transparent
                  ? COLOR.black
                  : colorCode,
              weight: 1,
            };
          },
          stateinfo: (properties, zoom) => {
            return {
              fill: true,
              fillColor: COLOR.transparent,
              fillOpacity: 1,
              stroke: true,
              color: "#00000040",
              weight: 1,
            };
          },
        },
      }
    );
    vectorTileLayer.addTo(map);

    setVectorTileLayer(vectorTileLayer);
    vectorTileLayer.on("click", (e) => {
      if (onSelectorAdded) onSelectorAdded(false);
      if (setMapClickedInfo)
        setMapClickedInfo({
          lat: e?.latlng?.lat,
          lng: e?.latlng?.lng,
          gridid: e?.layer?.properties.gridid,
        });
    });

    vectorTileLayer.on("load", function (e) {});
    vectorTileLayer.on("loading", function (e) {});
    vectorTileLayer.on("error", function (e) {});
  };
  useEffect(() => {
    addSelector();
  }, [selectedGeometry]);
};

export default StateVectorLayer;
